import { useState, FormEvent, ChangeEvent, useEffect } from "react";
import { ChevronLeft, Upload, X, Plus } from "lucide-react";
import { adminDashboard } from "../../services/api";
import { useAuth } from '../../context/AuthContext';

// Define interfaces for our state
interface Chapter {
  id: number;
  title: string;
  duration: string;
  description: string;
  videoFile: File | null;
  videoOriginalName: string;
  videoPreview?: string | null;
  ressources: ChapitreResource[];
}

interface ChapitreResource {
  id: number;
  title: string;
  description: string;
  type: string;
  file: File | null;
  originalName: string;
  filePreview?: string | null;
}

interface Section {
  id: number;
  title: string;
  duration: string;
  chapters: Chapter[];
}

interface Resource {
  id: number;
  title: string;
  description: string;
  type: string;
  file: File | null;
  originalName: string;
  filePreview?: string | null;
}

interface Category {
  id: number;
  name: string;
}

export default function FormateurAddCourse() {
  const { user, logout, loading } = useAuth();

  const [activeTab, setActiveTab] = useState<string>("info");
  const [learningObjectives, setLearningObjectives] = useState<string[]>([""]);
  const [sections, setSections] = useState<Section[]>([
    {
      id: 1,
      title: "",
      duration: "",
      chapters: [
        {
          id: 1,
          title: "",
          duration: "",
          description: "",
          videoFile: null,
          videoOriginalName: "",
          videoPreview: null,
          ressources: []
        }
      ]
    }
  ]);

  const [resources, setResources] = useState<Resource[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [publicationStatus, setPublicationStatus] = useState("Publié");
  const [priceType, setPriceType] = useState("free");
  const [amount, setAmount] = useState("");
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [videoDescriptionFile, setVideoDescriptionFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [videoDescriptionPreview, setVideoDescriptionPreview] = useState<string | null>(null);
  const [totalDuration, setTotalDuration] = useState<number>(0);

  useEffect(() => {
    const calculateTotalDuration = () => {
      const total = sections.reduce((sectionTotal, section) => {
        const sectionDuration = section.chapters.reduce((chapterTotal, chapter) => {
          return chapterTotal + (parseInt(chapter.duration) || 0);
        }, 0);
        return sectionTotal + sectionDuration;
      }, 0);
      setTotalDuration(total);
    };

    calculateTotalDuration();
  }, [sections]);

  // Form data states
  const [formData, setFormData] = useState({
    title: "",
    shortDescription: "",
    description: "",
    categoryId: "",
    newCategoryName: ""
  });

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    return () => {
      if (coverImagePreview) {
        URL.revokeObjectURL(coverImagePreview);
      }
    };
  }, [coverImagePreview]);

  useEffect(() => {
    return () => {
      // Clean up all resource preview URLs on unmount
      resources.forEach(resource => {
        if (resource.filePreview) {
          URL.revokeObjectURL(resource.filePreview);
        }
      });
    };
  }, []);

  useEffect(() => {
    return () => {
      if (videoDescriptionPreview) {
        URL.revokeObjectURL(videoDescriptionPreview);
      }
    };
  }, [videoDescriptionPreview]);

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await adminDashboard.getFormationsOverview();
        if (response.data.success) {
          setCategories(response.data.categories);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des catégories:", error);
      }
    };
    loadCategories();
  }, []);

  const handlePriceTypeChange = (value: string) => {
    setPriceType(value);
    if (value === "free") {
      setAmount("");
    }
  };

  const handleFormDataChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Add learning objective
  const addLearningObjective = (): void => {
    setLearningObjectives([...learningObjectives, ""]);
  };

  // Update learning objective
  const updateLearningObjective = (index: number, value: string): void => {
    const updatedObjectives = [...learningObjectives];
    updatedObjectives[index] = value;
    setLearningObjectives(updatedObjectives);
  };

  // Remove learning objective
  const removeLearningObjective = (index: number): void => {
    if (learningObjectives.length > 1) {
      const updatedObjectives = [...learningObjectives];
      updatedObjectives.splice(index, 1);
      setLearningObjectives(updatedObjectives);
    }
  };

  // Add section
  const addSection = (): void => {
    const newSectionId = sections.length > 0 ? Math.max(...sections.map(s => s.id)) + 1 : 1;
    setSections([...sections, {
      id: newSectionId,
      title: "",
      duration: "",
      chapters: []
    }]);
  };

  // Remove section
  const removeSection = (sectionId: number): void => {
    if (sections.length > 1) {
      setSections(sections.filter(section => section.id !== sectionId));
    }
  };

  // Update section
  const updateSection = (sectionId: number, field: string, value: string): void => {
    const updatedSections = sections.map(section => {
      if (section.id === sectionId) {
        return { ...section, [field]: value };
      }
      return section;
    });
    setSections(updatedSections);
  };

  // Add chapter to section
  const addChapter = (sectionId: number): void => {
    setSections(prevSections => {
      const updatedSections = [...prevSections];
      const sectionIndex = updatedSections.findIndex(section => section.id === sectionId);

      if (sectionIndex !== -1) {
        const newChapterId = updatedSections[sectionIndex].chapters.length > 0
          ? Math.max(...updatedSections[sectionIndex].chapters.map(c => c.id)) + 1
          : 1;

        updatedSections[sectionIndex].chapters.push({
          id: newChapterId,
          title: "",
          duration: "",
          description: "",
          videoFile: null,
          videoOriginalName: "",
          videoPreview: null,
          ressources: []
        });

        // Recalculate section duration
        const totalDuration = updatedSections[sectionIndex].chapters.reduce((total, chapter) => {
          return total + (parseInt(chapter.duration) || 0);
        }, 0);
        updatedSections[sectionIndex].duration = totalDuration.toString();
      }

      return updatedSections;
    });
  };

  // Remove chapter
  const removeChapter = (sectionId: number, chapterId: number): void => {
    setSections(prevSections => {
      const updatedSections = [...prevSections];
      const sectionIndex = updatedSections.findIndex(section => section.id === sectionId);

      if (sectionIndex !== -1 && updatedSections[sectionIndex].chapters.length > 1) {
        updatedSections[sectionIndex].chapters = updatedSections[sectionIndex].chapters.filter(
          chapter => chapter.id !== chapterId
        );

        // Recalculate section duration
        const totalDuration = updatedSections[sectionIndex].chapters.reduce((total, chapter) => {
          return total + (parseInt(chapter.duration) || 0);
        }, 0);
        updatedSections[sectionIndex].duration = totalDuration.toString();
      }

      return updatedSections;
    });
  };

  // Update chapter
  const updateChapter = (sectionId: number, chapterId: number, field: keyof Chapter, value: any): void => {
    setSections(prevSections => {
      const updatedSections = prevSections.map(section => {
        if (section.id === sectionId) {
          const updatedChapters = section.chapters.map(chapter => {
            if (chapter.id === chapterId) {
              return { ...chapter, [field]: value };
            }
            return chapter;
          });

          // Calculate new section duration immediately if duration field is being updated
          let newSectionDuration = section.duration;
          if (field === 'duration') {
            newSectionDuration = updatedChapters.reduce((total, chapter) => {
              return total + (parseInt(chapter.duration) || 0);
            }, 0).toString();
          }

          return {
            ...section,
            chapters: updatedChapters,
            duration: newSectionDuration
          };
        }
        return section;
      });

      return updatedSections;
    });
  };

  // Add resource
  const addResource = (): void => {
    const newResourceId = resources.length > 0 ? Math.max(...resources.map(r => r.id)) + 1 : 1;
    setResources([...resources, {
      id: newResourceId,
      title: "",
      description: "",
      type: "",
      file: null,
      originalName: "",
      filePreview: null
    }]);
  };

  // Remove resource
  const removeResource = (resourceId: number): void => {
    // Clean up preview URL before removing
    const resourceToRemove = resources.find(r => r.id === resourceId);
    if (resourceToRemove?.filePreview) {
      URL.revokeObjectURL(resourceToRemove.filePreview);
    }

    setResources(resources.filter(resource => resource.id !== resourceId));
  };

  // Update resource
  const updateResource = (resourceId: number, field: keyof Resource, value: any): void => {
    const updatedResources = resources.map(resource => {
      if (resource.id === resourceId) {
        if (field === 'file' && value instanceof File) {
          // Clean up previous preview URL
          if (resource.filePreview) {
            URL.revokeObjectURL(resource.filePreview);
          }

          // ✅ Auto-detect file type based on the uploaded file
          let detectedFileType = "Document";
          if (value.type.includes('pdf')) detectedFileType = "Document PDF";
          else if (value.type.includes('video')) detectedFileType = "Vidéo";
          else if (value.type.includes('image')) detectedFileType = "Image";
          else if (value.type.includes('word') || value.name.endsWith('.doc') || value.name.endsWith('.docx')) detectedFileType = "Document Word";
          else if (value.type.includes('powerpoint') || value.name.endsWith('.ppt') || value.name.endsWith('.pptx')) detectedFileType = "Présentation";

          return {
            ...resource,
            file: value,
            originalName: value.name,
            type: detectedFileType,  // ✅ Set the detected type
            filePreview: URL.createObjectURL(value)
          };
        }
        return { ...resource, [field]: value };
      }
      return resource;
    });
    setResources(updatedResources);
  };


  // Add chapitre resource
  const addChapitreResource = (sectionId: number, chapterId: number): void => {
    setSections(prevSections => {
      const updatedSections = [...prevSections];
      const sectionIndex = updatedSections.findIndex(s => s.id === sectionId);
      if (sectionIndex !== -1) {
        const chapterIndex = updatedSections[sectionIndex].chapters.findIndex(c => c.id === chapterId);
        if (chapterIndex !== -1) {
          const newResourceId = updatedSections[sectionIndex].chapters[chapterIndex].ressources.length > 0
            ? Math.max(...updatedSections[sectionIndex].chapters[chapterIndex].ressources.map(r => r.id)) + 1
            : 1;

          updatedSections[sectionIndex].chapters[chapterIndex].ressources.push({
            id: newResourceId,
            title: "",
            description: "",
            type: "",
            file: null,
            originalName: "",
            filePreview: null
          });
        }
      }
      return updatedSections;
    });
  };

  // Remove chapitre resource
  const removeChapitreResource = (sectionId: number, chapterId: number, resourceId: number): void => {
    setSections(prevSections => {
      const updatedSections = [...prevSections];
      const sectionIndex = updatedSections.findIndex(s => s.id === sectionId);
      if (sectionIndex !== -1) {
        const chapterIndex = updatedSections[sectionIndex].chapters.findIndex(c => c.id === chapterId);
        if (chapterIndex !== -1) {
          // Clean up preview URL
          const resourceToRemove = updatedSections[sectionIndex].chapters[chapterIndex].ressources.find(r => r.id === resourceId);
          if (resourceToRemove?.filePreview) {
            URL.revokeObjectURL(resourceToRemove.filePreview);
          }

          updatedSections[sectionIndex].chapters[chapterIndex].ressources =
            updatedSections[sectionIndex].chapters[chapterIndex].ressources.filter(r => r.id !== resourceId);
        }
      }
      return updatedSections;
    });
  };

  // Update chapitre resource
  const updateChapitreResource = (sectionId: number, chapterId: number, resourceId: number, field: keyof ChapitreResource, value: any): void => {
    setSections(prevSections => {
      const updatedSections = [...prevSections];
      const sectionIndex = updatedSections.findIndex(s => s.id === sectionId);
      if (sectionIndex !== -1) {
        const chapterIndex = updatedSections[sectionIndex].chapters.findIndex(c => c.id === chapterId);
        if (chapterIndex !== -1) {
          updatedSections[sectionIndex].chapters[chapterIndex].ressources =
            updatedSections[sectionIndex].chapters[chapterIndex].ressources.map(resource => {
              if (resource.id === resourceId) {
                if (field === 'file' && value instanceof File) {
                  // Clean up previous preview URL
                  if (resource.filePreview) {
                    URL.revokeObjectURL(resource.filePreview);
                  }

                  // Auto-detect file type
                  let detectedFileType = "Document";
                  if (value.type.includes('pdf')) detectedFileType = "Document PDF";
                  else if (value.type.includes('video')) detectedFileType = "Vidéo";
                  else if (value.type.includes('image')) detectedFileType = "Image";
                  else if (value.type.includes('word') || value.name.endsWith('.doc') || value.name.endsWith('.docx')) detectedFileType = "Document Word";
                  else if (value.type.includes('powerpoint') || value.name.endsWith('.ppt') || value.name.endsWith('.pptx')) detectedFileType = "Présentation";

                  return {
                    ...resource,
                    file: value,
                    originalName: value.name,
                    type: detectedFileType,
                    filePreview: URL.createObjectURL(value)
                  };
                }
                return { ...resource, [field]: value };
              }
              return resource;
            });
        }
      }
      return updatedSections;
    });
  };

  // Handle file inputs safely
  const handleFileInputClick = (elementId: string): void => {
    const element = document.getElementById(elementId);
    if (element) {
      element.click();
    }
  };

  // Validation functions
  const validateForm = (): string | null => {
    // Basic info validation
    if (!formData.title.trim()) return "Le titre de la formation est requis.";
    if (!formData.shortDescription.trim()) return "La description courte est requise.";
    if (!formData.description.trim()) return "La description détaillée est requise.";
    if (!formData.categoryId) return "La catégorie est requise.";
    if (formData.categoryId === "new" && !formData.newCategoryName?.trim()) {
      return "Le nom de la nouvelle catégorie est requis.";
    }
    if (!coverImage) return "L'image de couverture est requise.";

    // Learning objectives validation
    const validObjectives = learningObjectives.filter(obj => obj.trim() !== "");
    if (validObjectives.length === 0) return "Au moins un objectif d'apprentissage est requis.";

    // Price validation
    if (priceType === "paid" && (!amount || isNaN(Number(amount)) || Number(amount) <= 0)) {
      return "Le montant doit être un nombre valide pour une formation payante.";
    }

    // Sections validation
    if (sections.length === 0) return "Au moins une section est requise.";

    for (const section of sections) {
      if (!section.title.trim()) return "Chaque section doit avoir un titre.";
      if (!section.duration.trim()) return "Chaque section doit avoir une durée.";
      if (section.chapters.length === 0) return `La section "${section.title}" doit contenir au moins un chapitre.`;

      for (const chapter of section.chapters) {
        if (!chapter.title.trim()) return `Le chapitre dans la section "${section.title}" doit avoir un titre.`;
        if (!chapter.duration.trim()) return `Le chapitre "${chapter.title}" doit avoir une durée.`;
        if (!chapter.description.trim()) return `Le chapitre "${chapter.title}" doit avoir une description.`;
        // Validate chapitre resources (if any)
        for (const resource of chapter.ressources) {
          if (!resource.title.trim()) return `La ressource dans le chapitre "${chapter.title}" doit avoir un titre.`;
          if (!resource.description.trim()) return `La ressource "${resource.title}" dans le chapitre "${chapter.title}" doit avoir une description.`;
          if (!resource.file) return `La ressource "${resource.title}" dans le chapitre "${chapter.title}" doit avoir un fichier.`;
        }
      }
    }

    // Resources validation (if any resources are added)
    for (const resource of resources) {
      if (!resource.title.trim()) return "Chaque ressource doit avoir un titre.";
      if (!resource.description.trim()) return "Chaque ressource doit avoir une description.";
      if (!resource.file) return `La ressource "${resource.title}" doit avoir un fichier.`;
    }

    return null;
  };

  // Form submission
  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const submitFormData = new FormData();

      // Basic form data
      submitFormData.append('name', formData.title);
      submitFormData.append('shortDescription', formData.shortDescription);
      submitFormData.append('longDescription', formData.description);
      if (formData.categoryId === "new") {
        // Send new category name for backend to create
        submitFormData.append('categoryName', formData.newCategoryName);
      } else {
        // Send existing category ID
        submitFormData.append('categoryId', formData.categoryId);
      }
      submitFormData.append('gratuit', priceType === "free" ? 'true' : 'false');
      if (priceType === "paid") {
        submitFormData.append('price', amount);
      }
      submitFormData.append('currency', 'MAD');
      submitFormData.append('status', publicationStatus);

      if (user?.id) {
        submitFormData.append('formateur_id', user.id.toString());
      }

      // Cover image
      if (coverImage) {
        submitFormData.append('image', coverImage);
      }

      // Video description (optional)
      if (videoDescriptionFile) {
        submitFormData.append('videoDescription', videoDescriptionFile);
      }

      submitFormData.append('totalDuration', totalDuration.toString());

      // Learning objectives
      const validObjectives = learningObjectives.filter(obj => obj.trim() !== "");
      const apprendreData = validObjectives.map(obj => ({ learningText: obj }));
      submitFormData.append('apprendre', JSON.stringify(apprendreData));

      // Sections data
      const sectionsData = sections.map((section, index) => ({
        number: index + 1,
        title: section.title,
        durationMinutes: parseInt(section.duration) || 0
      }));
      submitFormData.append('sections', JSON.stringify(sectionsData));

      // Chapters data and videos
      const chaptersData: any[] = [];
      sections.forEach((section, sectionIndex) => {
        section.chapters.forEach((chapter, chapterIndex) => {
          chaptersData.push({
            number: chapterIndex + 1,
            title: chapter.title,
            durationMinutes: parseInt(chapter.duration) || 0,
            description: chapter.description,
            sectionNumber: sectionIndex + 1,
            videoOriginalName: chapter.videoFile ? chapter.videoOriginalName : ''
          });

          if (chapter.videoFile) {
            submitFormData.append('chapitreVideos', chapter.videoFile);
          }
        });
      });
      submitFormData.append('chapitres', JSON.stringify(chaptersData));

      // Resources data and files
      if (resources.length > 0) {
        const resourcesData = resources.map(resource => ({
          name: resource.title,
          description: resource.description,
          fileType: resource.type,
          originalName: resource.originalName
        }));
        submitFormData.append('ressources', JSON.stringify(resourcesData));

        resources.forEach(resource => {
          if (resource.file) {
            submitFormData.append('ressourceFiles', resource.file);
          }
        });
      } else {
        submitFormData.append('ressources', JSON.stringify([]));
      }

      // Chapitre resources data and files
      const chapitreResourcesData: any[] = [];
      sections.forEach((section, sectionIndex) => {
        section.chapters.forEach((chapter, chapterIndex) => {
          chapter.ressources.forEach(resource => {
            chapitreResourcesData.push({
              chapitreNumber: chapterIndex + 1,
              name: resource.title,
              description: resource.description,
              fileType: resource.type,
              originalName: resource.originalName
            });

            if (resource.file) {
              submitFormData.append('chapitreRessourceFiles', resource.file);
            }
          });
        });
      });

      if (chapitreResourcesData.length > 0) {
        submitFormData.append('chapitreRessources', JSON.stringify(chapitreResourcesData));
      } else {
        submitFormData.append('chapitreRessources', JSON.stringify([]));
      }

      // Submit to API
      const response = await adminDashboard.createFormation(submitFormData);

      if (response.data.success) {
        setSuccessMessage("Formation créée avec succès!");
        setShowSuccessPopup(true);

        // Reset form after successful submission
        setTimeout(() => {
          window.location.href = "/formateur/courses";
        }, 2000);
      } else {
        setErrorMessage(response.data.message || "Erreur lors de la création de la formation.");
      }
    } catch (error: any) {
      console.error("Erreur lors de la soumission:", error);
      setErrorMessage(
        error.response?.data?.message ||
        "Erreur lors de la création de la formation. Veuillez réessayer."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <a href="/formateur/courses" className="flex items-center text-[#00088E] mb-4">
            <ChevronLeft className="w-4 h-4 mr-1" />
            <span>Retour aux formations</span>
          </a>
          <h1 className="text-3xl font-bold mb-2">Ajouter une formation</h1>
          <p className="text-gray-600">Créez une nouvelle formation en remplissant les informations ci-dessous.</p>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
            <strong>Erreur:</strong> {errorMessage}
          </div>
        )}

        {/* Form tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="flex border-b">
            <button
              className={`flex-1 py-4 px-4 text-center ${activeTab === "info" ? "border-b-2 border-[#00088E] text-[#00088E] font-medium" : "text-gray-500"}`}
              onClick={() => setActiveTab("info")}
            >
              Informations générales
            </button>
            <button
              className={`flex-1 py-4 px-4 text-center ${activeTab === "content" ? "border-b-2 border-[#00088E] text-[#00088E] font-medium" : "text-gray-500"}`}
              onClick={() => setActiveTab("content")}
            >
              Contenu
            </button>
            <button
              className={`flex-1 py-4 px-4 text-center ${activeTab === "resources" ? "border-b-2 border-[#00088E] text-[#00088E] font-medium" : "text-gray-500"}`}
              onClick={() => setActiveTab("resources")}
            >
              Ressources
            </button>
            <button
              className={`flex-1 py-4 px-4 text-center ${activeTab === "settings" ? "border-b-2 border-[#00088E] text-[#00088E] font-medium" : "text-gray-500"}`}
              onClick={() => setActiveTab("settings")}
            >
              Paramètres
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Tab Content */}
          {activeTab === "info" && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <h2 className="text-xl font-bold mb-4">Informations de base</h2>
              <p className="text-gray-600 mb-6">Entrez les informations principales concernant cette formation.</p>

              <div className="space-y-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Titre de la formation *
                  </label>
                  <input
                    type="text"
                    id="title"
                    placeholder="Ex: Introduction aux droits humains"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.title}
                    onChange={(e) => handleFormDataChange('title', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    Description courte *
                  </label>
                  <textarea
                    id="shortDescription"
                    placeholder="Une brève description de moins de 200 caractères"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={3}
                    value={formData.shortDescription}
                    onChange={(e) => handleFormDataChange('shortDescription', e.target.value)}
                    maxLength={200}
                    required
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.shortDescription.length}/200 caractères</p>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description détaillée *
                  </label>
                  <textarea
                    id="description"
                    placeholder="Une description complète de la formation, ses objectifs et son contenu"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={6}
                    value={formData.description}
                    onChange={(e) => handleFormDataChange('description', e.target.value)}
                    required
                  ></textarea>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Category Selection */}
                  <div>
                    <label htmlFor="categorySelect" className="block text-sm font-medium text-gray-700 mb-1">
                      Catégorie *
                    </label>
                    <select
                      id="categorySelect"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={formData.categoryId}
                      onChange={(e) => handleFormDataChange('categoryId', e.target.value)}
                      required
                    >
                      <option value="">Sélectionnez une catégorie</option>
                      <option value="new">+ Créer une nouvelle catégorie</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id.toString()}>
                          {category.name}
                        </option>
                      ))}
                    </select>

                    {formData.categoryId === "new" && (
                      <div className="mt-2">
                        <input
                          type="text"
                          placeholder="Nom de la nouvelle catégorie"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          value={formData.newCategoryName || ""}
                          onChange={(e) => handleFormDataChange('newCategoryName', e.target.value)}
                          required
                        />
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {formData.categoryId === "new"
                        ? "Entrez le nom de la nouvelle catégorie"
                        : "Sélectionnez une catégorie existante ou créez-en une nouvelle"
                      }
                    </p>
                  </div>
                </div>

                <div>
                  <label htmlFor="courseImage" className="block text-sm font-medium text-gray-700 mb-1">
                    Image de couverture *
                  </label>
                  <div className="border border-dashed border-gray-300 rounded-md bg-gray-50 p-8 text-center">
                    <div className="flex flex-col items-center">
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500 mb-1">
                        {coverImage ? coverImage.name : "Sélectionnez une image"}
                      </p>
                      {coverImagePreview && (
                        <>
                          <img
                            src={coverImagePreview}
                            alt="Aperçu de l'image de couverture"
                            className="mt-2 max-h-48 rounded-md object-contain"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setCoverImage(null);
                              setCoverImagePreview(null);
                            }}
                            className="mt-2 px-3 py-1 text-sm bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200"
                          >
                            Supprimer l'image sélectionnée
                          </button>
                        </>
                      )}
                      <p className="text-xs text-gray-400 mb-2">PNG, JPG ou JPEG (max. 2Mo)</p>
                      <input
                        type="file"
                        id="courseImage"
                        className="hidden"
                        accept="image/png, image/jpeg, image/jpg"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setCoverImage(file);

                            // ✅ Generate preview URL
                            const previewURL = URL.createObjectURL(file);
                            setCoverImagePreview(previewURL);
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleFileInputClick('courseImage')}
                        className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Parcourir
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                <label htmlFor="videoDescriptionFile" className="block text-sm font-medium text-gray-700 mb-1">
                  Vidéo de description (optionnel)
                </label>
                <div className="border border-dashed border-gray-300 rounded-md bg-gray-50 p-8 text-center">
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500 mb-1">
                      {videoDescriptionFile ? videoDescriptionFile.name : "Sélectionnez une vidéo"}
                    </p>
                    {videoDescriptionPreview && (
                      <>
                        <video
                          controls
                          src={videoDescriptionPreview}
                          className="mt-2 max-h-48 rounded-md object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setVideoDescriptionFile(null);
                            setVideoDescriptionPreview(null);
                          }}
                          className="mt-2 px-3 py-1 text-sm bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200"
                        >
                          Supprimer la vidéo sélectionnée
                        </button>
                      </>
                    )}
                    <p className="text-xs text-gray-400 mb-2">Formats acceptés : MP4, MOV, AVI (max. 50Mo)</p>
                    <input
                      type="file"
                      id="videoDescriptionFile"
                      className="hidden"
                      accept="video/mp4, video/quicktime, video/x-msvideo"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setVideoDescriptionFile(file);
                          const previewURL = URL.createObjectURL(file);
                          setVideoDescriptionPreview(previewURL);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleFileInputClick('videoDescriptionFile')}
                      className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Parcourir
                    </button>
                  </div>
                </div>
              </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Ce que les apprenants vont acquérir *
                  </label>
                  <p className="text-sm text-gray-500 mb-4">Listez les compétences ou connaissances que les apprenants acquerront.</p>

                  <div className="space-y-3">
                    {learningObjectives.map((objective, index) => (
                      <div key={index} className="flex items-center">
                        <input
                          type="text"
                          value={objective}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => updateLearningObjective(index, e.target.value)}
                          placeholder="Point d'apprentissage"
                          className="flex-grow px-3 py-2 border border-gray-300 rounded-md"
                          required={index === 0}
                        />
                        {learningObjectives.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLearningObjective(index)}
                            className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addLearningObjective}
                    className="mt-3 px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-600 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    <span>Ajouter un objectif</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "content" && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <h2 className="text-xl font-bold mb-4">Structure du cours</h2>
              <p className="text-gray-600 mb-6">Définissez les sections et chapitres de la formation. Au moins une section avec un chapitre est requise.</p>

              <div className="space-y-8">
                {sections.map((section, sectionIndex) => (
                  <div key={section.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-grow">
                        <div>
                          <label htmlFor={`section-${section.id}-title`} className="block text-sm font-medium text-gray-700 mb-1">
                            Titre de la section *
                          </label>
                          <input
                            type="text"
                            id={`section-${section.id}-title`}
                            placeholder="Ex: Introduction aux droits humains"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            value={section.title}
                            onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Durée totale de la section (minutes)
                          </label>
                          <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100">
                            {section.chapters.reduce((total, chapter) => total + (parseInt(chapter.duration) || 0), 0)} minutes
                          </div>
                          {/* Hidden input to ensure duration is included in form data */}
                          <input
                            type="hidden"
                            value={section.chapters.reduce((total, chapter) => total + (parseInt(chapter.duration) || 0), 0)}
                            onChange={() => {}}
                          />
                        </div>
                      </div>
                      {sections.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSection(section.id)}
                          className="ml-4 px-3 py-1 border border-gray-300 rounded flex items-center text-sm"
                        >
                          <span>Supprimer</span>
                        </button>
                      )}
                    </div>

                    <h3 className="font-medium mb-3">Chapitres (minimum 1 requis)</h3>

                    <div className="space-y-6">
                      {section.chapters.map((chapter, chapterIndex) => (
                        <div key={chapter.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="font-medium">Chapitre {chapterIndex + 1}</h4>
                            {section.chapters.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeChapter(section.id, chapter.id)}
                                className="text-sm text-gray-500 flex items-center"
                              >
                                <X className="w-4 h-4 mr-1" />
                                <span>Supprimer</span>
                              </button>
                            )}
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label htmlFor={`chapter-${section.id}-${chapter.id}-title`} className="block text-sm font-medium text-gray-700 mb-1">
                                Titre du chapitre *
                              </label>
                              <input
                                type="text"
                                id={`chapter-${section.id}-${chapter.id}-title`}
                                placeholder="Ex: Qu'est-ce que les droits humains?"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={chapter.title}
                                onChange={(e) => updateChapter(section.id, chapter.id, 'title', e.target.value)}
                                required
                              />
                            </div>

                            <div>
                              <label htmlFor={`chapter-${section.id}-${chapter.id}-duration`} className="block text-sm font-medium text-gray-700 mb-1">
                                Durée (en minutes) *
                              </label>
                              <input
                                type="number"
                                id={`chapter-${section.id}-${chapter.id}-duration`}
                                placeholder="Ex: 30"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={chapter.duration}
                                onChange={(e) => {
                                  updateChapter(section.id, chapter.id, 'duration', e.target.value);
                                }}
                                required
                              />
                            </div>

                            <div>
                              <label htmlFor={`chapter-${section.id}-${chapter.id}-description`} className="block text-sm font-medium text-gray-700 mb-1">
                                Description *
                              </label>
                              <textarea
                                id={`chapter-${section.id}-${chapter.id}-description`}
                                placeholder="Description du chapitre"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                rows={3}
                                value={chapter.description}
                                onChange={(e) => updateChapter(section.id, chapter.id, 'description', e.target.value)}
                                required
                              ></textarea>
                            </div>

                            <div>
                              <label htmlFor={`chapter-${section.id}-${chapter.id}-video`} className="block text-sm font-medium text-gray-700 mb-1">
                                Vidéo du chapitre (optionnel)
                              </label>
                              <div className="border border-dashed border-gray-300 rounded-md bg-gray-50 p-6 text-center">
                                <div className="flex flex-col items-center">
                                  <Upload className="w-6 h-6 text-gray-400 mb-2" />
                                  <p className="text-sm text-gray-500 mb-1">
                                    {chapter.videoFile ? chapter.videoFile.name : "Glisser-déposer un fichier ici (optionnel)"}
                                  </p>
                                  <p className="text-xs text-gray-400 mb-2">MP4, MAX 500 Mo — laisser vide pour afficher uniquement le texte du chapitre</p>
                                  <input
                                    type="file"
                                    id={`chapter-${section.id}-${chapter.id}-video`}
                                    className="hidden"
                                    accept="video/mp4"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        updateChapter(section.id, chapter.id, 'videoFile', file);
                                        updateChapter(section.id, chapter.id, 'videoOriginalName', file.name);
                                        updateChapter(section.id, chapter.id, 'videoPreview', URL.createObjectURL(file));  // ✅ Generate preview
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleFileInputClick(`chapter-${section.id}-${chapter.id}-video`)}
                                    className="px-4 py-1.5 text-sm bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                  >
                                    Parcourir
                                  </button>

                                  {chapter.videoPreview && (
                                    <>
                                      <video
                                        controls
                                        src={chapter.videoPreview}
                                        className="mt-2 max-h-48 rounded-md object-contain"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (chapter.videoPreview) URL.revokeObjectURL(chapter.videoPreview);
                                          updateChapter(section.id, chapter.id, 'videoFile', null);
                                          updateChapter(section.id, chapter.id, 'videoOriginalName', '');
                                          updateChapter(section.id, chapter.id, 'videoPreview', null);
                                        }}
                                        className="mt-2 px-3 py-1 text-sm bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200"
                                      >
                                        Supprimer la vidéo
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Chapitre Resources Section */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-3">
                                Ressources du chapitre (optionnel)
                              </label>

                              {chapter.ressources.length === 0 ? (
                                <div className="text-center py-4 bg-gray-50 rounded-lg">
                                  <p className="text-gray-500 text-sm mb-2">Aucune ressource ajoutée</p>
                                  <button
                                    type="button"
                                    onClick={() => addChapitreResource(section.id, chapter.id)}
                                    className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-600 flex items-center mx-auto"
                                  >
                                    <Plus className="w-4 h-4 mr-1" />
                                    <span>Ajouter une ressource</span>
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {chapter.ressources.map((resource) => (
                                    <div key={resource.id} className="border border-gray-200 rounded p-4">
                                      <div className="flex justify-between items-center mb-3">
                                        <h5 className="text-sm font-medium">Ressource {resource.id}</h5>
                                        <button
                                          type="button"
                                          onClick={() => removeChapitreResource(section.id, chapter.id, resource.id)}
                                          className="text-sm text-gray-500 flex items-center"
                                        >
                                          <X className="w-4 h-4 mr-1" />
                                          <span>Supprimer</span>
                                        </button>
                                      </div>

                                      <div className="space-y-3">
                                        <div>
                                          <input
                                            type="text"
                                            placeholder="Titre de la ressource"
                                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                                            value={resource.title}
                                            onChange={(e) => updateChapitreResource(section.id, chapter.id, resource.id, 'title', e.target.value)}
                                          />
                                        </div>

                                        <div>
                                          <textarea
                                            placeholder="Description de la ressource"
                                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                                            rows={2}
                                            value={resource.description}
                                            onChange={(e) => updateChapitreResource(section.id, chapter.id, resource.id, 'description', e.target.value)}
                                          />
                                        </div>

                                        <div>
                                          <div className="border border-dashed border-gray-300 rounded bg-gray-50 p-4 text-center">
                                            <div className="flex flex-col items-center">
                                              <Upload className="w-5 h-5 text-gray-400 mb-1" />
                                              <p className="text-xs text-gray-500 mb-1">
                                                {resource.file ? resource.file.name : "Fichier de ressource"}
                                              </p>
                                              <input
                                                type="file"
                                                id={`chapitre-resource-${section.id}-${chapter.id}-${resource.id}`}
                                                className="hidden"
                                                onChange={(e) => {
                                                  const file = e.target.files?.[0];
                                                  if (file) {
                                                    updateChapitreResource(section.id, chapter.id, resource.id, 'file', file);
                                                  }
                                                }}
                                              />
                                              <button
                                                type="button"
                                                onClick={() => handleFileInputClick(`chapitre-resource-${section.id}-${chapter.id}-${resource.id}`)}
                                                className="px-3 py-1 text-xs bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                                              >
                                                Parcourir
                                              </button>

                                              {resource.filePreview && (
                                                <>
                                                  {resource.file?.type.startsWith("image/") ? (
                                                    <img src={resource.filePreview} alt="Aperçu" className="mt-2 max-h-24 rounded object-contain" />
                                                  ) : resource.file?.type === "application/pdf" ? (
                                                    <embed src={resource.filePreview} type="application/pdf" className="mt-2 w-full h-32 rounded" />
                                                  ) : resource.file?.type.startsWith("video/") ? (
                                                    <video controls src={resource.filePreview} className="mt-2 max-h-24 rounded object-contain" />
                                                  ) : (
                                                    <p className="text-xs text-gray-500 mt-2">Aperçu non disponible</p>
                                                  )}

                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      if (resource.filePreview) URL.revokeObjectURL(resource.filePreview);
                                                      updateChapitreResource(section.id, chapter.id, resource.id, 'file', null);
                                                      updateChapitreResource(section.id, chapter.id, resource.id, 'originalName', '');
                                                      updateChapitreResource(section.id, chapter.id, resource.id, 'filePreview', null);
                                                    }}
                                                    className="mt-1 px-2 py-0.5 text-xs bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200"
                                                  >
                                                    Supprimer
                                                  </button>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}

                                  <button
                                    type="button"
                                    onClick={() => addChapitreResource(section.id, chapter.id)}
                                    className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-600 flex items-center"
                                  >
                                    <Plus className="w-4 h-4 mr-1" />
                                    <span>Ajouter une ressource</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() => addChapter(section.id)}
                        className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-600 flex items-center"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        <span>Ajouter un chapitre</span>
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addSection}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-600 flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  <span>Ajouter une section</span>
                </button>
              </div>
            </div>
          )}

          {activeTab === "resources" && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <h2 className="text-xl font-bold mb-4">Ressources pédagogiques</h2>
              <p className="text-gray-600 mb-6">Ajoutez des ressources complémentaires pour les apprenants (optionnel).</p>

              <div className="space-y-6">
                {resources.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 mb-4">Aucune ressource ajoutée</p>
                    <button
                      type="button"
                      onClick={addResource}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-600 flex items-center mx-auto"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      <span>Ajouter une ressource</span>
                    </button>
                  </div>
                ) : (
                  <>
                    {resources.map((resource) => (
                      <div key={resource.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-medium">Ressource {resource.id}</h4>
                          <button
                            type="button"
                            onClick={() => removeResource(resource.id)}
                            className="text-sm text-gray-500 flex items-center"
                          >
                            <X className="w-4 h-4 mr-1" />
                            <span>Supprimer</span>
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label htmlFor={`resource-${resource.id}-title`} className="block text-sm font-medium text-gray-700 mb-1">
                              Titre de la ressource *
                            </label>
                            <input
                              type="text"
                              id={`resource-${resource.id}-title`}
                              placeholder="Ex: Guide des droits humains fondamentaux"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              value={resource.title}
                              onChange={(e) => updateResource(resource.id, 'title', e.target.value)}
                              required
                            />
                          </div>

                          <div>
                            <label htmlFor={`resource-${resource.id}-description`} className="block text-sm font-medium text-gray-700 mb-1">
                              Description *
                            </label>
                            <textarea
                              id={`resource-${resource.id}-description`}
                              placeholder="Description de la ressource"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              rows={3}
                              value={resource.description}
                              onChange={(e) => updateResource(resource.id, 'description', e.target.value)}
                              required
                            ></textarea>
                          </div>

                          <div>
                            <label htmlFor={`resource-${resource.id}-file`} className="block text-sm font-medium text-gray-700 mb-1">
                              Fichier de la ressource *
                            </label>
                            <div className="border border-dashed border-gray-300 rounded-md bg-gray-50 p-6 text-center">
                              <div className="flex flex-col items-center">
                                <Upload className="w-6 h-6 text-gray-400 mb-2" />
                                <p className="text-sm text-gray-500 mb-1">
                                  {resource.file ? resource.file.name : "Glisser-déposer un fichier ici"}
                                </p>
                                <p className="text-xs text-gray-400 mb-2">PDF, DOC, PPT, etc. (max. 100Mo)</p>
                                <input
                                  type="file"
                                  id={`resource-${resource.id}-file`}
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      updateResource(resource.id, 'file', file);
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleFileInputClick(`resource-${resource.id}-file`)}
                                  className="px-4 py-1.5 text-sm bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                  Parcourir
                                </button>

                                {resource.filePreview && (
                                  <>
                                    {resource.file?.type.startsWith("image/") ? (
                                      <img
                                        src={resource.filePreview}
                                        alt="Aperçu du fichier"
                                        className="mt-2 max-h-48 rounded-md object-contain"
                                      />
                                    ) : resource.file?.type === "application/pdf" ? (
                                      <embed
                                        src={resource.filePreview}
                                        type="application/pdf"
                                        className="mt-2 w-full h-96 rounded-md"
                                      />
                                    ) : resource.file?.type.startsWith("video/") ? (
                                      <video
                                        controls
                                        src={resource.filePreview}
                                        className="mt-2 max-h-48 rounded-md object-contain"
                                      />
                                    ) : (
                                      <p className="text-xs text-gray-500 mt-2">Aperçu non disponible pour ce type de fichier.</p>
                                    )}

                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (resource.filePreview) URL.revokeObjectURL(resource.filePreview);
                                        updateResource(resource.id, 'file', null);
                                        updateResource(resource.id, 'originalName', '');
                                        updateResource(resource.id, 'filePreview', null);
                                      }}
                                      className="mt-2 px-3 py-1 text-sm bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200"
                                    >
                                      Supprimer le fichier
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addResource}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-600 flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      <span>Ajouter une ressource</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === "settings" && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <h2 className="text-xl font-bold mb-4">Paramètres de la formation</h2>
              <p className="text-gray-600 mb-6">Configurez les paramètres de publication et d'accès.</p>

              <div className="space-y-6">
                <div>
                  <label htmlFor="publicationStatus" className="block text-sm font-medium text-gray-700 mb-1">
                    Statut de publication
                  </label>
                  <select
                    id="publicationStatus"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={publicationStatus}
                    onChange={(e) => setPublicationStatus(e.target.value)}
                  >
                    <option value="Archivé">Archivé</option>
                    <option value="Publié">Publié</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                    Prix
                  </label>
                  <select
                    id="price"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={priceType}
                    onChange={(e) => handlePriceTypeChange(e.target.value)}
                  >
                    <option value="free">Gratuit</option>
                    <option value="paid">Payant</option>
                  </select>
                </div>

                {priceType === "paid" && (
                  <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                      Montant (MAD) *
                    </label>
                    <div className="flex">
                      <input
                        type="number"
                        id="amount"
                        placeholder="Ex: 299"
                        className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        min="0"
                        step="0.01"
                        required
                      />
                      <span className="inline-flex items-center px-3 py-2 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500">
                        MAD
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
              onClick={() => window.history.back()}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting || loading}
              className="px-6 py-2 bg-[#00088E] text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Création en cours..." : "Créer la formation"}
            </button>
          </div>
        </form>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full mx-4 animate-in fade-in duration-300">
            <div className="flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-1">Succès</h3>
              <p className="text-gray-500 dark:text-gray-400">{successMessage}</p>
              <p className="text-sm text-gray-400 mt-2">Redirection en cours...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
